import { Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppService } from '../../../app.service';
import { UsersComponentComponent } from '../../../users-component/users-component.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

/**
 * Angular 12 migration of admin.users-list-module (Users Access Report).
 * Original: loadGridJson?action_id=32938&grid_id=6013
 * Filter bar: refresh, type dropdown (PwC Users / Non-PwC Users / All).
 * Grid callback: userDetail opens user edit modal (same as admin/users-list).
 */
@Component({
    selector: 'gtw-users-access-report',
    templateUrl: './users-access-report.component.html',
    styleUrls: ['./users-access-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UsersAccessReportComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;

  userTypes = [
    { label: 'PwC Users', value: '0' },
    { label: 'Non-PwC Users', value: '1' },
    { label: 'All', value: '2' },
  ];
  selectedType = '0';

  private fallbackClientKey = '';

  private readonly ACTION_ID = 32938;
  private readonly GRID_ID = '6013';
  /** Legacy `admin.users-list-module` → `users-list-new-tpl.html` + `UsersCtrl.js` (`#screen_key`). */
  private readonly EXPORT_SCREEN_KEY = '11505';

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private dialog: MatDialog,
    private snackbar: GtwSnackbarService,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.initClientKey();
    this.buildGridConfig();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    if (!this.isUserDetailCallback(event.callName)) {
      return;
    }
    switch (event.type) {
      case 'form':
        this.openUserEditModal(event.data);
        break;
      default:
        this.openUserEditModal(event.data);
        break;
    }
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ user_key: this.selectedType });
  }

  onTypeChange(): void {
    this.gridConfig = {
      ...this.gridConfig,
      extraParams: { user_key: this.selectedType },
    };
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ user_key: this.selectedType });
  }

  private buildGridConfig(): void {
    this.gridConfig = {
      actionId: this.ACTION_ID,
      gridId: this.GRID_ID,
      extraParams: { user_key: this.selectedType },
      autoLoad: true,
      cacheKey: 'admin/users-access-report',
      exportScreenKey: this.EXPORT_SCREEN_KEY,
    };
  }

  private initClientKey(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      const clientKey = userSettings.user.client?.client_key ?? userSettings.user.client_key;
      this.fallbackClientKey = (clientKey != null) ? String(clientKey) : '';
    }
  }

  private isUserDetailCallback(callName: string): boolean {
    return callName === 'userDetail' || callName === 'userDetailModule';
  }

  private openUserEditModal(eventData: any): void {
    const row = eventData?.rowData ?? eventData ?? {};
    const sso =
      row.SSO_ID ||
      row.SSO ||
      row.user_id ||
      row.sso_id ||
      row.USER_ID ||
      row.USER_SSO;
    if (!sso) {
      this.snackbar.openSnackBar(
        'Cannot open user details: missing SSO_ID for the selected row.',
        'gtw-snackbar--red'
      );
      this.cdr.detectChanges();
      return;
    }
    const rowData = {
      user_id: sso,
      selected_client_key: row.CLIENT_KEY ?? this.fallbackClientKey,
      ...row,
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = ['admin-user-edit-panel', 'increase-z-index'];
    dialogConfig.width = '90vw';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.position = { top: '20px' };
    dialogConfig.disableClose = true;
    dialogConfig.data = { rowData };

    this.adminScreenContext.setRowData(rowData);
    const dialogRef = this.dialog.open(UsersComponentComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.adminScreenContext.setRowData({});
      this.adminGrid?.clearCache();
      this.adminGrid?.refresh({ user_key: this.selectedType });
      this.cdr.detectChanges();
    });
  }
}
